import useUserData from 'hooks/useUserData';
import BuildingLineIcon from 'remixicon-react/BuildingLineIcon';
import FileListLineIcon from 'remixicon-react/FileListLineIcon';
import FoldersLineIcon from 'remixicon-react/FoldersLineIcon';
import GroupLineIcon from 'remixicon-react/GroupLineIcon';
import HandCoinLineIcon from 'remixicon-react/HandCoinLineIcon';
import FileLineIcon from 'remixicon-react/FileLineIcon';
import { useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import {
  JAVA_SERVICE_ENDPOINTS,
  PYTHON_SERVICE_ENDPOINTS,
} from '@buildwise/libs/services/endpoints';
import { BUILDWISE_MODULES, BUILDWISE_MODULES_FEATURES } from 'components/drawer/constants';
import SearchAPI, { GlobalSearchPayload } from 'api/SearchAPI';
import PythonSearchAPI from 'api/PythonSearchAPI';
import { useSearchParams } from 'react-router-dom';
import { useBuildWiseModules } from '@buildwise/libs/helpers';

export type SearchTab = {
  disabled: boolean;
  emptyDataIcon: JSX.Element;
  endpoint: string;
  service: typeof SearchAPI | typeof PythonSearchAPI;
  icon: JSX.Element;
  name: string;
  payload: GlobalSearchPayload;
  type: string;
};

function useSearchTabs() {
  const [searchParams] = useSearchParams();
  const searchKeyword = decodeURIComponent(searchParams.get('q') || '');
  const theme = useTheme();
  const { company, companyId } = useUserData();
  const {
    projectsModule,
    pluralPartnersModuleName,
    pluralProjectsModuleName,
    singularProceduresModuleName,
    projectModuleFeatures,
    partnersModule,
    proceduresModule,
  } = useBuildWiseModules();
  const SEARCH_TABS = useMemo(
    () =>
      (
        [
          {
            type: 'articles',
            name: 'Posts',
            icon: <FileListLineIcon size={18} />,
            disabled: false,
            emptyDataIcon: <FileListLineIcon size={140} color={theme.palette.custom.steel} />,
            endpoint: searchKeyword?.startsWith('#')
              ? PYTHON_SERVICE_ENDPOINTS.ARTICLES_WITH_HASHTAG_SEARCH
              : PYTHON_SERVICE_ENDPOINTS.ARTICLES_SEARCH,
            service: PythonSearchAPI,
            payload: {
              ...(searchKeyword?.startsWith('#')
                ? { companyId }
                : {
                    companyId,
                    novaSearchType: 'articles',
                    fileSearch: !!company?.settings?.articlesLLMSearch,
                    pageContent: false,
                  }),
            },
          },
          {
            type: 'users',
            name: 'People',
            icon: <GroupLineIcon size={18} />,
            disabled: false,
            emptyDataIcon: <GroupLineIcon size={140} color={theme.palette.custom.steel} />,
            endpoint: JAVA_SERVICE_ENDPOINTS.SEARCH,
            service: SearchAPI,
            payload: {
              companyId,
              novaSearchType: 'users',
            },
          },
          {
            type: 'projects',
            name: pluralProjectsModuleName || BUILDWISE_MODULES.PROJECTS,
            icon: <BuildingLineIcon size={18} />,
            disabled: !projectsModule?.isActive,
            emptyDataIcon: <BuildingLineIcon size={140} color={theme.palette.custom.steel} />,
            endpoint: JAVA_SERVICE_ENDPOINTS.SEARCH,
            service: SearchAPI,
            payload: {
              companyId,
              novaSearchType: 'projects',
            },
          },
          {
            type: 'stakeholders',
            name: pluralPartnersModuleName || BUILDWISE_MODULES.PARTNERS,
            icon: <HandCoinLineIcon size={18} />,
            disabled: !partnersModule?.isActive,
            emptyDataIcon: <HandCoinLineIcon size={140} color={theme.palette.custom.steel} />,
            endpoint: JAVA_SERVICE_ENDPOINTS.SEARCH,
            service: SearchAPI,
            payload: {
              companyId,
              novaSearchType: 'stakeholders',
            },
          },
          {
            type: 'companyFiles',
            name: singularProceduresModuleName || BUILDWISE_MODULES.PROCEDURES,
            icon: <FoldersLineIcon size={18} />,
            disabled: !proceduresModule?.isActive,
            emptyDataIcon: <FoldersLineIcon size={140} color={theme.palette.custom.steel} />,
            endpoint: PYTHON_SERVICE_ENDPOINTS.COMPANY_FILES_SEARCH,
            service: PythonSearchAPI,
            payload: {
              companyId,
              novaSearchType: 'companyFiles',
              summarization: false,
              pageContent: false,
            },
          },
          {
            type: 'projectFileSearch',
            name: 'Project Files',
            icon: <FileLineIcon size={18} />,
            disabled: !projectModuleFeatures[BUILDWISE_MODULES_FEATURES.PROJECTS.FILES]?.isActive,
            emptyDataIcon: <FileLineIcon size={140} color={theme.palette.custom.steel} />,
            endpoint: PYTHON_SERVICE_ENDPOINTS.PROJECT_FILES_SEARCH,
            service: PythonSearchAPI,
            payload: {
              companyId,
              novaSearchType: 'projectFileSearch',
              summarization: false,
              pageContent: false,
            },
          },
        ] as SearchTab[]
      ).filter((tab) => !tab.disabled),
    [
      projectModuleFeatures,
      proceduresModule,
      partnersModule,
      projectsModule,
      company,
      companyId,
      searchKeyword,
      projectsModule,
      pluralPartnersModuleName,
      pluralProjectsModuleName,
      singularProceduresModuleName,
    ],
  );

  return {
    SEARCH_TABS,
  };
}

export default useSearchTabs;
